import { useQuery } from "@tanstack/react-query";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { ContentAssetRow } from "./types";

// GET /api/admin/content/assets?folder=&archived=&type=&q=
//
// Phase 7 sub-bucket 13. Single global cache key (`queryKeys.content.list()`)
// — every Phase 6 mutation hook already invalidates this key, so the list
// refreshes on create/update/move/archive/delete without per-hook wiring.
// Filter state lives in URL params on the page; the query refetches when
// the URL changes via the `filters` object identity (the params object is
// rebuilt each render only when one of the params actually changes, so the
// effective key is stable in practice).
//
// `folder` semantics (mirrors the API):
//   - undefined  → root (server interprets missing param as parent_asset_id IS NULL)
//   - "__all__"  → whole library (q-driven search across folders)
//   - <uuid>     → that folder
export type ContentAssetsFilters = {
  folder?: string;
  archived?: boolean;
  type?: string;
  q?: string;
};

function toQueryString(filters: ContentAssetsFilters): string {
  const sp = new URLSearchParams();
  if (filters.folder !== undefined) sp.set("folder", filters.folder);
  if (filters.archived) sp.set("archived", "1");
  if (filters.type && filters.type !== "all") sp.set("type", filters.type);
  if (filters.q && filters.q.trim().length > 0) sp.set("q", filters.q.trim());
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export function useContentAssets(filters: ContentAssetsFilters = {}) {
  return useQuery<ContentAssetRow[], ApiClientError>({
    queryKey: [
      ...queryKeys.content.list(),
      filters.folder ?? null,
      Boolean(filters.archived),
      filters.type ?? null,
      filters.q ?? null,
    ],
    queryFn: () =>
      apiFetch<ContentAssetRow[]>(`/admin/content/assets${toQueryString(filters)}`),
  });
}
